import React, { useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import {
  Calendar3,
  ChevronLeft,
  ChevronRight,
  Clock,
  Display,
  Download,
  Link45deg,
  Paperclip,
  PlayFill,
} from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import ProtectedPromptVideo from './ProtectedPromptVideo';
import './UserProfile.css';

interface Question {
  id: number;
  title: string;
  description: string;
  documents: string[];
}

interface Prompt {
  id: number;
  content: string;
  description: string;
  fileUrl: string;
  supportedDocumentUrl: string | null;
  visibility?: string;
  is_locked?: boolean;
  isLocked?: boolean;
  has_video?: boolean;
  hasVideo?: boolean;
}

const CandidatePublicScreeningPrompts: React.FC<{
  prompts?: Prompt[];
  iOwnTheAccount?: boolean;
  iNeedAccessToView?: string;
  hasFullAccess?: boolean;
  candidateId?: number;
  viewerType?: 'recruiter' | 'candidate_self' | 'anonymous';
  setPromptsData?: (prompts: Question[]) => void;
  shareToken?: string | null;
  candidateUsername?: string;
  candidateDisplayName?: string;
  onRequestFullAccess?: () => void;
}> = ({
  prompts,
  iNeedAccessToView,
  viewerType = 'anonymous',
  iOwnTheAccount,
  shareToken,
  candidateUsername = '',
  candidateDisplayName = '',
  onRequestFullAccess,
}) => {
  const isReadOnlyPreview = viewerType === 'candidate_self' || Boolean(iOwnTheAccount);
  const [selectedPromptId, setSelectedPromptId] = useState<number | null>(prompts?.[0]?.id ?? null);

  const visiblePrompts = useMemo(() => {
    const list = prompts || [];
    if (iOwnTheAccount) return list;
    return list.filter((prompt) => (prompt.visibility ?? 'public').toLowerCase() !== 'private');
  }, [prompts, iOwnTheAccount]);
  const selectedPrompt = useMemo(
    () =>
      visiblePrompts.find((prompt) => prompt.id === selectedPromptId) || visiblePrompts[0] || null,
    [visiblePrompts, selectedPromptId]
  );

  const selectedPromptIndex = useMemo(() => {
    if (!selectedPrompt) return 0;
    return visiblePrompts.findIndex((prompt) => prompt.id === selectedPrompt.id) + 1;
  }, [selectedPrompt, visiblePrompts]);

  const hasPreviousPrompt = selectedPromptIndex > 1;
  const hasNextPrompt = selectedPromptIndex > 0 && selectedPromptIndex < visiblePrompts.length;

  const supportingDocuments = useMemo(() => {
    const docMap = new Map<string, { url: string; name: string }>();
    visiblePrompts.forEach((prompt) => {
      if (!prompt.supportedDocumentUrl) return;
      const url = prompt.supportedDocumentUrl;
      const fileName = url.split('/').pop() || 'Supporting document';
      docMap.set(url, { url, name: fileName });
    });
    return Array.from(docMap.values());
  }, [visiblePrompts]);

  const handleCopyPublicLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Public profile link copied');
    } catch {
      toast.error('Unable to copy link');
    }
  };

  const goPrev = () => {
    if (!selectedPrompt || !hasPreviousPrompt) return;
    const prevPrompt = visiblePrompts[selectedPromptIndex - 2];
    if (prevPrompt) setSelectedPromptId(prevPrompt.id);
  };

  const goNext = () => {
    if (!selectedPrompt || !hasNextPrompt) return;
    const nextPrompt = visiblePrompts[selectedPromptIndex];
    if (nextPrompt) setSelectedPromptId(nextPrompt.id);
  };

  return (
    <div className="recruiter-candidate-prompts">
      {!isReadOnlyPreview ? (
        <div className="public-profile-notice">
          <div className="notice-copy">
            {iNeedAccessToView === 'NO' ? (
              <>
                <span className="notice-badge notice-badge-access">Recruiter Access</span>
                <span>You have verified recruiter access to this candidate profile.</span>
              </>
            ) : (
              <>
                <span className="notice-badge">Public Profile</span>
                <span>This is the public candidate profile. Anyone with the link can view.</span>
              </>
            )}
          </div>
          <Button variant="link" className="notice-copy-link" onClick={handleCopyPublicLink}>
            <Link45deg size={16} className="me-1" /> Copy Link
          </Button>
        </div>
      ) : null}

      <div className="candidate-prompts-grid">
        <div className="candidate-prompts-main">
          {!selectedPrompt ? (
            <div className="prompt-focus-card prompt-empty-state">
              No screening prompts are available for this candidate yet.
            </div>
          ) : (
            <div className="prompt-focus-card">
              <div className="prompt-focus-header">
                <div className="prompt-focus-meta">
                  PROMPT {selectedPromptIndex} OF {visiblePrompts.length}
                </div>
                <div className="prompt-sequence-controls">
                  <button
                    type="button"
                    className="prompt-sequence-button"
                    onClick={goPrev}
                    disabled={!hasPreviousPrompt}
                  >
                    <ChevronLeft size={14} /> Previous
                  </button>
                  <button
                    type="button"
                    className="prompt-sequence-button"
                    onClick={goNext}
                    disabled={!hasNextPrompt}
                  >
                    Next <ChevronRight size={14} />
                  </button>
                </div>
              </div>

              <h2 className="prompt-focus-title">{selectedPrompt.content}</h2>
              <div className="prompt-video-shell">
                {selectedPrompt.is_locked || selectedPrompt.isLocked ? (
                  <div className="prompt-locked-card">
                    <div className="prompt-locked-title">Recruiter Only</div>
                    <p className="prompt-locked-copy">
                      This Screening Prompt requires Full Access. Request Full Access from the
                      candidate to view this response.
                    </p>
                    {!iOwnTheAccount ? (
                      <button
                        type="button"
                        className="candidate-primary-button"
                        onClick={() => onRequestFullAccess?.()}
                      >
                        Request Full Access
                      </button>
                    ) : null}
                  </div>
                ) : selectedPrompt.fileUrl ||
                  selectedPrompt.has_video ||
                  selectedPrompt.hasVideo ? (
                  <ProtectedPromptVideo
                    username={candidateUsername}
                    promptId={selectedPrompt.id}
                    candidateName={candidateDisplayName}
                    shareToken={shareToken}
                  />
                ) : (
                  <div className="prompt-video-placeholder">
                    <div className="prompt-video-placeholder-avatar">👤</div>
                    <div className="prompt-video-placeholder-play">
                      <PlayFill size={18} />
                    </div>
                  </div>
                )}
              </div>

              <div className="prompt-meta-row">
                <span className="prompt-meta-pill">
                  <Clock size={13} /> Video Response
                </span>
                <span className="prompt-meta-pill">
                  <Calendar3 size={13} /> Public Profile
                </span>
                <span className="prompt-meta-pill">
                  <Display size={13} /> SkipScreen Hosted
                </span>
              </div>

              <div className="prompt-focus-detail-grid">
                <div className="prompt-detail-card">
                  <div className="prompt-detail-title">Prompt Description</div>
                  <div className="prompt-detail-copy">
                    {selectedPrompt.description || 'No description provided.'}
                  </div>
                </div>
                <div className="prompt-detail-card">
                  <div className="prompt-detail-title">Supporting Documents</div>
                  {selectedPrompt.supportedDocumentUrl ? (
                    <a
                      href={selectedPrompt.supportedDocumentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="support-doc-card"
                    >
                      <span className="support-doc-name">
                        {selectedPrompt.supportedDocumentUrl.split('/').pop() ||
                          'Supporting document'}
                      </span>
                      <Download size={15} />
                    </a>
                  ) : (
                    <div className="support-doc-empty">
                      No supporting document attached to this prompt.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {visiblePrompts.map((prompt, index) => {
            const isSelected = prompt.id === selectedPrompt?.id;
            return (
              <button
                type="button"
                key={prompt.id}
                className={`prompt-summary-card ${isSelected ? 'prompt-summary-card-active' : ''}`}
                onClick={() => setSelectedPromptId(prompt.id)}
              >
                <div>
                  <div className="prompt-summary-meta">PROMPT {index + 1}</div>
                  <div className="prompt-summary-title">{prompt.content}</div>
                </div>
                <ChevronRight size={16} />
              </button>
            );
          })}
        </div>

        <aside className="candidate-quick-actions">
          <div className="quick-actions-card">
            <div className="quick-actions-card-head">
              <div className="quick-actions-title-row">
                <Paperclip size={15} />
                <span>Supporting Documents</span>
              </div>
              <span className="quick-actions-counter">{supportingDocuments.length} available</span>
            </div>
            <div className="quick-documents-list">
              {supportingDocuments.length > 0 ? (
                supportingDocuments.map((document) => (
                  <a
                    key={document.url}
                    href={document.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="quick-document-item"
                  >
                    <div className="quick-document-icon">PDF</div>
                    <div className="quick-document-copy">
                      <div className="quick-document-name">{document.name}</div>
                      <div className="quick-document-meta">Supporting file</div>
                    </div>
                    <Download size={14} />
                  </a>
                ))
              ) : (
                <div className="support-doc-empty">
                  No supporting documents are currently available.
                </div>
              )}
            </div>
          </div>

          <div className="quick-tip-card">
            <div className="quick-tip-icon">💡</div>
            <div>
              <div className="quick-tip-title">Pro Tip</div>
              <p className="quick-tip-copy">
                Ask follow-up questions to learn more about specific experience or achievements.
              </p>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default CandidatePublicScreeningPrompts;
